import { useState } from 'react';
import type { ReactElement } from 'react';
import { useSignInMutation } from '@/api/endpoints';
import { Button } from '@/design/Button';
import { Logo } from '@/design/Logo';
import { Field, TextInput } from '@/design/Field';
import { EnvironmentBanner } from '@/layout/EnvironmentBanner';
import { readApiError } from '@/lib/apiError';

/**
 * Signing in.
 *
 * Phone number, not email: the people who use this have a phone. The number
 * field is `type="tel"` so a phone shows a number pad, and it accepts whatever
 * shape they type — the server normalises it.
 *
 * No logo wall, no marketing, no illustration. This screen is a door.
 *
 * The door carries the product's name, GadiDesk, and nobody's business name:
 * it is the same door for every tenant, and which counter is behind it is not
 * known until the phone number has been matched to an account.
 */
export function SignInPage(): ReactElement {
  const [signIn, { isLoading, error }] = useSignInMutation();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  const problems = readApiError(error);

  return (
    <div className="min-h-dvh bg-paper">
      {/* Before the password, not after. Signing in is the moment two
          identical-looking systems are easiest to confuse. */}
      <EnvironmentBanner />
      <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-4 py-10">
        <div className="mb-6 flex justify-center">
          <Logo size={30} />
        </div>

        <div className="border border-rule bg-panel">
          {/* The one marker again: this is the app's signature, not decoration. */}
          <div className="border-b border-rule border-l-[3px] border-l-signal px-5 py-4 pl-[calc(1.25rem-3px)]">
            {/* No business name here, and not for want of one: the same door
                serves every tenant now, and which one it opens onto is not
                known until the phone number has been matched. */}
            <h1 className="text-heading font-semibold">Counter sign-in</h1>
            <p className="text-small text-ink-muted">Sign in with your phone number</p>
          </div>

          <form
            className="flex flex-col gap-4 px-5 py-5"
            onSubmit={(event) => {
              event.preventDefault();
              void signIn({ phone, password });
            }}
          >
            {problems.detail !== undefined && (
              <p
                className="border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
                role="alert"
              >
                {problems.detail}
              </p>
            )}

            <Field label="Phone number" error={problems.fields['phone']}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  describedBy={describedBy}
                  type="tel"
                  autoComplete="username"
                  autoFocus
                  value={phone}
                  onChange={setPhone}
                />
              )}
            </Field>

            <Field label="Password" error={problems.fields['password']}>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  describedBy={describedBy}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={setPassword}
                />
              )}
            </Field>

            <Button
              intent="primary"
              size="touch"
              type="submit"
              disabled={isLoading || phone.trim() === '' || password === ''}
            >
              {isLoading ? 'Signing in' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-small text-ink-muted">
          Forgotten your password? The owner can set a new one.
        </p>
      </main>
    </div>
  );
}
